import {inject} from '@angular/core';
import {CanDeactivateFn} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {SmConfirmationDialogService} from '../../components/sm-confirmation-dialog/sm-confirmation-dialog.service';

export interface ProtectedEditor {
  readonly hasUnsavedChanges: boolean;
  readonly isSaving: boolean;
  readonly leaveConfirmed: boolean;
  discardRefreshDraft(): void;
}

const pendingConfirmations = new WeakMap<ProtectedEditor, Promise<boolean>>();

export const editorLeaveGuard: CanDeactivateFn<ProtectedEditor> = (editor) => {
  if (editor.leaveConfirmed) {
    return true;
  }
  if (editor.isSaving) {
    return false;
  }
  if (!editor.hasUnsavedChanges) {
    return true;
  }
  const pending = pendingConfirmations.get(editor);
  if (pending) {
    return pending;
  }
  const dialog = inject(SmConfirmationDialogService);
  const translate = inject(TranslateService);
  const confirmation = dialog.open({
    title: translate.instant('common.editorCancelWarning.title'),
    compactActions: true,
    actionsAlign: 'center',
    description: translate.instant('common.editorCancelWarning.description'),
    cancelLabel: translate.instant('common.editorCancelWarning.cancelLabel'),
    confirmLabel: translate.instant('common.editorCancelWarning.confirmLabel'),
    closeLabel: translate.instant('common.editorCancelWarning.closeLabel'),
    confirmTone: 'danger',
    heroIconName: 'warning-outline',
  }).then((confirmed) => {
    if (confirmed) {
      editor.discardRefreshDraft();
    }
    return confirmed;
  }).finally(() => pendingConfirmations.delete(editor));
  pendingConfirmations.set(editor, confirmation);
  return confirmation;
};
